import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@centraoneplus/ui';
import {
  TriggerType,
  ConditionOperator,
  Condition,
  ConditionGroup,
} from '@/services/workflow.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface ConditionBuilderProps {
  conditionGroup: ConditionGroup;
  onChange: (conditionGroup: ConditionGroup) => void;
  triggerType?: TriggerType;
  compact?: boolean; // Compact mode for use inside branching actions
}

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  [ConditionOperator.EQUALS]: 'equals',
  [ConditionOperator.NOT_EQUALS]: 'does not equal',
  [ConditionOperator.CONTAINS]: 'contains',
  [ConditionOperator.NOT_CONTAINS]: 'does not contain',
  [ConditionOperator.STARTS_WITH]: 'starts with',
  [ConditionOperator.ENDS_WITH]: 'ends with',
  [ConditionOperator.GREATER_THAN]: 'greater than',
  [ConditionOperator.LESS_THAN]: 'less than',
  [ConditionOperator.IN]: 'is in list',
  [ConditionOperator.NOT_IN]: 'is not in list',
  [ConditionOperator.IS_EMPTY]: 'is empty',
  [ConditionOperator.IS_NOT_EMPTY]: 'is not empty',
};

// No-value operators don't need the value input
const NO_VALUE_OPERATORS = [ConditionOperator.IS_EMPTY, ConditionOperator.IS_NOT_EMPTY];
const LIST_OPERATORS = [ConditionOperator.IN, ConditionOperator.NOT_IN];

// Predefined value options for fields with known system values
const FIELD_VALUE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  status: [
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'pending', label: 'Pending' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ],
  priority: [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ],
  channel: [
    { value: 'email', label: 'Email' },
    { value: 'chat', label: 'Chat' },
    { value: 'phone', label: 'Phone' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'api', label: 'API' },
  ],
  type: [
    { value: 'question', label: 'Question' },
    { value: 'incident', label: 'Incident' },
    { value: 'problem', label: 'Problem' },
    { value: 'task', label: 'Task' },
    { value: 'request', label: 'Request' },
  ],
  'ai.intent': [
    { value: 'question', label: 'Question' },
    { value: 'complaint', label: 'Complaint' },
    { value: 'request_support', label: 'Request Support' },
    { value: 'request_feature', label: 'Request Feature' },
    { value: 'report_bug', label: 'Report Bug' },
    { value: 'request_refund', label: 'Request Refund' },
    { value: 'update_account', label: 'Update Account' },
    { value: 'cancel_service', label: 'Cancel Service' },
    { value: 'get_information', label: 'Get Information' },
    { value: 'provide_feedback', label: 'Provide Feedback' },
    { value: 'other', label: 'Other' },
  ],
  'ai.sentiment': [
    { value: 'positive', label: 'Positive' },
    { value: 'negative', label: 'Negative' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'frustrated', label: 'Frustrated' },
    { value: 'angry', label: 'Angry' },
  ],
  'ai.category': [
    { value: 'billing', label: 'Billing' },
    { value: 'technical', label: 'Technical' },
    { value: 'account', label: 'Account' },
    { value: 'product', label: 'Product' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'returns', label: 'Returns' },
    { value: 'sales', label: 'Sales' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'general', label: 'General' },
    { value: 'other', label: 'Other' },
  ],
  'ai.suggestedPriority': [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ],
};

// Field suggestions based on trigger type category
const TRIGGER_FIELD_MAP: Record<string, string[]> = {
  ticket: ['status', 'priority', 'channel', 'subject', 'assignedTo', 'tags', 'customerEmail', 'type', 'ticketTypeId', 'entity.lineItemDetails', 'ai.intent', 'ai.sentiment', 'ai.category', 'ai.intentConfidence', 'ai.suggestedPriority', 'ai.suggestedQueue'],
  customer: ['email', 'name', 'type', 'company', 'tags'],
  chat: ['channel', 'status', 'agentId', 'customerEmail'],
  email: ['from', 'to', 'subject', 'body'],
  default: ['status', 'priority', 'channel'],
};

function getTriggerCategory(triggerType: TriggerType): string {
  if (triggerType.startsWith('ticket_')) return 'ticket';
  if (triggerType.startsWith('customer_')) return 'customer';
  if (triggerType.startsWith('chat_')) return 'chat';
  if (triggerType === TriggerType.EMAIL_RECEIVED) return 'email';
  return 'default';
}

export default function ConditionBuilder({ conditionGroup, onChange, triggerType, compact = false }: ConditionBuilderProps) {
  const category = triggerType ? getTriggerCategory(triggerType) : 'default';
  const fieldSuggestions = TRIGGER_FIELD_MAP[category] || TRIGGER_FIELD_MAP.default;

  const updateLogic = (logic: 'AND' | 'OR') => {
    onChange({ ...conditionGroup, logic });
  };

  const addCondition = () => {
    const defaultField = fieldSuggestions[0] || 'status';
    const newCondition: Condition = {
      field: defaultField,
      operator: ConditionOperator.EQUALS,
      value: FIELD_VALUE_OPTIONS[defaultField]?.[0]?.value ?? '',
    };
    onChange({
      ...conditionGroup,
      conditions: [...conditionGroup.conditions, newCondition],
    });
  };

  const removeCondition = (index: number) => {
    const updated = [...conditionGroup.conditions];
    updated.splice(index, 1);
    onChange({ ...conditionGroup, conditions: updated });
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const updated = [...conditionGroup.conditions];
    updated[index] = { ...updated[index], ...updates };
    // Clear value when switching to no-value operator
    if (updates.operator && NO_VALUE_OPERATORS.includes(updates.operator)) {
      delete updated[index].value;
    }
    // Reset value when field changes between dropdown and free-text types
    if (updates.field) {
      const hasOptions = !!FIELD_VALUE_OPTIONS[updates.field];
      const hadOptions = !!FIELD_VALUE_OPTIONS[conditionGroup.conditions[index].field];
      if (hasOptions !== hadOptions || (hasOptions && updates.field !== conditionGroup.conditions[index].field)) {
        updated[index].value = hasOptions ? FIELD_VALUE_OPTIONS[updates.field][0].value : '';
      }
    }
    onChange({ ...conditionGroup, conditions: updated });
  };

  return (
    <div>
      <div className={`flex items-center justify-between ${compact ? 'mb-1' : 'mb-2'}`}>
        {!compact && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Conditions <span className="text-gray-400 font-normal dark:text-gray-500">(optional)</span>
          </label>
        )}
        <div className={`flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-md p-0.5 ${compact ? 'ml-auto' : ''}`}>
          <button
            type="button"
            onClick={() => updateLogic('AND')}
            className={`px-2 py-0.5 text-xs font-medium rounded ${
              conditionGroup.logic === 'AND'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => updateLogic('OR')}
            className={`px-2 py-0.5 text-xs font-medium rounded ${
              conditionGroup.logic === 'OR'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            OR
          </button>
        </div>
      </div>

      {conditionGroup.conditions.length === 0 ? (
        <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-gray-400 italic mb-2`}>
          {compact ? 'No conditions — always matches' : 'No conditions — rule will match all events of this trigger type'}
        </p>
      ) : (
        <div className={`space-y-2 ${compact ? 'mb-1' : 'mb-2'}`}>
          {conditionGroup.conditions.map((condition, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
            >
              <div className="flex items-center gap-2">
                {/* Field */}
                <ThemedSelect
                  value={condition.field}
                  onChange={(e) => updateCondition(index, { field: e.target.value })}
                  className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-2 py-1.5 text-sm"
                >
                  {fieldSuggestions.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                  {/* Show current field if it's not in suggestions (custom field) */}
                  {!fieldSuggestions.includes(condition.field) && (
                    <option value={condition.field}>{condition.field}</option>
                  )}
                </ThemedSelect>

                {/* Operator */}
                <ThemedSelect
                  value={condition.operator}
                  onChange={(e) =>
                    updateCondition(index, { operator: e.target.value as ConditionOperator })
                  }
                  className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-2 py-1.5 text-sm"
                >
                  {Object.entries(OPERATOR_LABELS).map(([op, label]) => (
                    <option key={op} value={op}>
                      {label}
                    </option>
                  ))}
                </ThemedSelect>

                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeCondition(index)}
                  className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0 dark:text-gray-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Value */}
              {!NO_VALUE_OPERATORS.includes(condition.operator) && (
                <div className="mt-2">
                  {FIELD_VALUE_OPTIONS[condition.field] && !LIST_OPERATORS.includes(condition.operator) ? (
                    <ThemedSelect
                      value={String(condition.value ?? '')}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-2 py-1.5 text-sm"
                    >
                      <option value="">Select value...</option>
                      {FIELD_VALUE_OPTIONS[condition.field].map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  ) : (
                    <input
                      type="text"
                      value={String(condition.value ?? '')}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      placeholder={
                        LIST_OPERATORS.includes(condition.operator)
                          ? 'Comma-separated values (e.g., open, pending)'
                          : 'Value'
                      }
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-2 py-1.5 text-sm"
                    />
                  )}
                </div>
              )}

              {/* Logic connector label between conditions */}
              {index < conditionGroup.conditions.length - 1 && (
                <div className="flex justify-center mt-2">
                  <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase">
                    {conditionGroup.logic}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Button variant={compact ? 'ghost' : 'outline'} size="sm" onClick={addCondition} type="button">
        <Plus className="w-3 h-3 mr-1" />
        {compact ? 'Add' : 'Add Condition'}
      </Button>
    </div>
  );
}
